import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type FormEvent,
} from "react";
import { Knopf } from "./Knopf";
import { IconAgent } from "./Symbole";
import { Freigabekarte } from "./assistent/Freigabekarte";
import { Zugblock } from "./assistent/Zugblock";
import { useArbeitskontext } from "./assistent/kontext";
import {
  abonniere,
  entscheideFreigabe,
  gesamtkostenCent,
  ladeKatalog,
  lies,
  neuesGespraech,
  sendeFrage,
  stelleWiederHer,
} from "./assistent/sitzung";
import {
  installiereTastenkuerzel,
  kuerzelText,
  nimmFokusAnforderung,
  schliesseAssistent,
} from "./assistent/tastatur";
import { euro } from "./assistent/texte";

/**
 * The assistant panel.
 *
 * Three things separate this from a chat box.
 *
 * It shows the work, not only the answer. Every lookup the assistant made is a line in
 * the transcript that opens onto what was asked and what came back, so a Verwalter can
 * see which records a number rests on — and can see when a number rests on none.
 *
 * It stops at the gate. Anything that would change data appears here as a card with
 * the exact change spelled out and two buttons beside it, in the place where the run
 * stopped. That is the four-eyes principle rendered as UI, and it is why it is not a
 * toast.
 *
 * It knows where the user is standing. The panel reads the current screen and the
 * record on it, so "was ist hier auffällig?" needs no preamble.
 *
 * The panel is a view over a store that outlives it (assistent/sitzung.ts): the shell
 * unmounts this component when the sidebar is collapsed, and a run must survive that.
 */

// Installed at module load, not in an effect: the shell unmounts this component when
// the panel is closed, so a listener owned by the component could never open it.
installiereTastenkuerzel();

export function Assistent() {
  const sitzung = useSyncExternalStore(abonniere, lies);
  const [eingabe, setEingabe] = useState("");
  const kontext = useArbeitskontext();

  const eingabeRef = useRef<HTMLTextAreaElement>(null);
  const laufRef = useRef<HTMLDivElement>(null);
  const amEndeRef = useRef(true);

  useEffect(() => {
    void ladeKatalog();
    // A reload must not lose a run that is still working. The transcript is rebuilt
    // from the run row, not from anything this tab remembered.
    void stelleWiederHer();
  }, []);

  // Focus only when the panel was opened by the shortcut. Any other appearance —
  // a click, a re-render, a navigation — leaves the caret in the grid where it was.
  useEffect(() => {
    if (nimmFokusAnforderung()) eingabeRef.current?.focus();
  }, []);

  // Follow the run only while the reader is already at the bottom. Yanking the view
  // back down while somebody is reading an earlier step is how a long run becomes
  // unusable.
  //
  // The container is scrolled directly rather than via scrollIntoView, because
  // scrollIntoView walks every scrollable ancestor: a growing transcript would drag
  // the whole work surface up with it, scrolling a grid the user is reading.
  useLayoutEffect(() => {
    const flaeche = laufRef.current;
    if (flaeche && amEndeRef.current) flaeche.scrollTop = flaeche.scrollHeight;
  }, [sitzung.zuege]);

  const beobachteScroll = useCallback(() => {
    const flaeche = laufRef.current;
    if (!flaeche) return;
    amEndeRef.current =
      flaeche.scrollHeight - flaeche.scrollTop - flaeche.clientHeight < 48;
  }, []);

  const absenden = useCallback(
    (text: string) => {
      const frage = text.trim();
      if (!frage || sitzung.sendet) return;
      setEingabe("");
      amEndeRef.current = true;
      void sendeFrage(frage, {
        liegenschaftId: kontext.liegenschaftId,
        periode: kontext.periode,
        label: kontext.label,
        kontext: {
          bildschirm: kontext.bildschirm,
          bezeichnung: kontext.satz,
          liegenschaftId: kontext.liegenschaftId,
          schadenId: kontext.schadenId,
          periode: kontext.periode,
        },
      });
    },
    [kontext, sitzung.sendet],
  );

  const gesamt = gesamtkostenCent(sitzung);

  return (
    <div
      className="flex flex-col h-full min-h-0"
      // The shell's content row is a full viewport tall and sits below a 44px bar, so
      // the last 44px of this column — the composer — falls under the fold. Pinning
      // the panel to the viewport keeps the input reachable without asking the shell
      // to change, and keeps it in place while the work surface scrolls behind it.
      style={{ position: "sticky", top: "44px", maxHeight: "calc(100dvh - 44px)" }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          schliesseAssistent();
        }
      }}
    >
      <header
        className="h-[34px] shrink-0 flex items-center gap-[6px] px-[12px] border-b"
        style={{ borderColor: "var(--line-hairline)", background: "var(--surface-sunken)" }}
      >
        <span style={{ color: "var(--agent-accent)" }}>
          <IconAgent size={13} />
        </span>
        <span
          className="text-[length:var(--text-2xs)]"
          style={{
            color: "var(--text-caps)",
            textTransform: "var(--label-transform)" as never,
            letterSpacing: "var(--tracking-caps)",
            fontWeight: "var(--weight-medium)",
          }}
        >
          Assistent
        </span>

        {gesamt > 0 ? (
          <span
            className="num ml-auto text-[length:var(--text-3xs)]"
            style={{ color: "var(--text-muted)" }}
            title="Kosten dieses Gesprächs"
          >
            {euro(gesamt)}
          </span>
        ) : null}

        <button
          type="button"
          onClick={neuesGespraech}
          className={
            "h-[20px] px-[6px] text-[length:var(--text-3xs)] border rounded-[var(--radius-1)] " +
            (gesamt > 0 ? "" : "ml-auto")
          }
          style={{ borderColor: "var(--line-hairline)", color: "var(--text-secondary)" }}
        >
          Neu
        </button>
      </header>

      <Kontextleiste label={kontext.label} />

      <div
        ref={laufRef}
        onScroll={beobachteScroll}
        className="flex-1 min-h-0 overflow-y-auto px-[12px] py-[10px] flex flex-col gap-[16px]"
      >
        {sitzung.zuege.length === 0 ? (
          <Einstieg bildschirm={kontext.bildschirm} onFrage={absenden} />
        ) : null}

        {sitzung.zuege.map((zug) => (
          <Zugblock
            key={zug.id}
            zug={zug}
            katalog={sitzung.katalog}
            freigabe={(freigabeId, titel, erledigt) => {
              const zustand = freigabeId ? sitzung.freigabeZustand[freigabeId] : undefined;
              return (
                <Freigabekarte
                  titel={titel}
                  daten={freigabeId ? (sitzung.freigaben[freigabeId] ?? null) : null}
                  entschieden={zustand?.entschieden ?? null}
                  erledigt={erledigt}
                  fehler={zustand?.fehler ?? null}
                  laeuft={zustand?.laeuft ?? false}
                  onEntscheiden={(entscheidung, notiz) => {
                    if (!freigabeId) return;
                    amEndeRef.current = true;
                    void entscheideFreigabe(freigabeId, entscheidung, notiz);
                  }}
                />
              );
            }}
          />
        ))}
      </div>

      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          absenden(eingabe);
        }}
        className="shrink-0 border-t p-[8px] flex flex-col gap-[4px]"
        style={{ borderColor: "var(--line-hairline)" }}
      >
        <label htmlFor="assistent-eingabe" className="sr-only">
          Frage an den Assistenten
        </label>
        <textarea
          ref={eingabeRef}
          id="assistent-eingabe"
          value={eingabe}
          onChange={(e) => setEingabe(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends, Shift+Enter breaks the line — the convention of every
            // messaging tool these people already use.
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              absenden(eingabe);
            }
          }}
          rows={3}
          placeholder="Frage zum Bestand"
          className="w-full resize-none border px-[8px] py-[6px] text-[length:var(--text-sm)] rounded-[var(--radius-2)]"
          style={{
            background: "var(--surface-field)",
            borderColor: "var(--line-field)",
            color: "var(--text-primary)",
          }}
        />
        <div className="flex items-center gap-[6px]">
          <span className="text-[length:var(--text-3xs)]" style={{ color: "var(--text-muted)" }}>
            Enter sendet · Umschalt+Enter neue Zeile · {kuerzelText()} schließt
          </span>
          <Knopf
            type="submit"
            variante="primaer"
            groesse="klein"
            laeuft={sitzung.sendet}
            disabled={!eingabe.trim()}
            className="ml-auto"
          >
            Senden
          </Knopf>
        </div>
      </form>
    </div>
  );
}

/**
 * What the assistant can see from here.
 *
 * Stated rather than implied. A panel that silently knows which property is on screen
 * is a panel whose answers cannot be predicted; one that says so lets the user decide
 * whether to name a different one.
 */
function Kontextleiste({ label }: { label: string }) {
  return (
    <div
      className="shrink-0 px-[12px] py-[4px] border-b flex items-baseline gap-[6px]"
      style={{ borderColor: "var(--line-hairline)", background: "var(--agent-quiet)" }}
    >
      <span
        className="text-[length:var(--text-3xs)] shrink-0"
        style={{
          color: "var(--text-caps)",
          textTransform: "var(--label-transform)" as never,
          letterSpacing: "var(--tracking-caps)",
        }}
      >
        Kontext
      </span>
      <span
        className="text-[length:var(--text-2xs)] truncate"
        style={{ color: "var(--text-secondary)" }}
        title={label}
      >
        {label}
      </span>
    </div>
  );
}

/**
 * The empty state.
 *
 * One sentence about what the thing does, then questions this assistant can genuinely
 * answer against the current screen. They are buttons, not illustrations — clicking
 * one asks it.
 */
function Einstieg({
  bildschirm,
  onFrage,
}: {
  bildschirm: string;
  onFrage: (text: string) => void;
}) {
  const fragen =
    bildschirm === "Objektakte"
      ? [
          "Was ist bei dieser Liegenschaft auffällig?",
          "Welche Fristen laufen hier ab?",
          "Zeig mir die Schadensgeschichte dieses Objekts.",
        ]
      : [
          "Welche Fristen sind überfällig?",
          "Wie hoch sind die offenen Posten insgesamt?",
          "Welche Liegenschaften hatten zuletzt Wasserschäden?",
        ];

  return (
    <div className="flex flex-col gap-[8px]">
      <p
        className="text-[length:var(--text-sm)]"
        style={{ color: "var(--text-secondary)", lineHeight: "var(--leading-snug)" }}
      >
        Der Assistent liest Ihren Bestand und zeigt zu jeder Zahl, wo sie herkommt. Was er
        verändern würde, legt er Ihnen vorher zur Freigabe vor.
      </p>
      <ul className="flex flex-col gap-[2px]">
        {fragen.map((frage) => (
          <li key={frage}>
            <button
              type="button"
              onClick={() => onFrage(frage)}
              className="text-left text-[length:var(--text-xs)] underline underline-offset-2"
              style={{ color: "var(--text-link)" }}
            >
              {frage}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
